import React, { createContext, useContext, useReducer, useEffect, useMemo, useState } from 'react';
import { trackAddToCart } from '../utils/analytics';

export type CartItem = {
  skuId: string;
  productName: string;
  variantName?: string;
  qty: number;
  unitPriceCents: number;
  image?: string;
  slug?: string;
  isKit?: boolean;
  includes?: string[];
};

type CartState = {
  items: CartItem[];
};

type AddItemPayload = Omit<CartItem, 'qty'> & { qty?: number };

type CartAction =
  | { type: 'ADD_ITEM'; payload: AddItemPayload }
  | { type: 'REMOVE_ITEM'; payload: { skuId: string } }
  | { type: 'SET_QTY'; payload: { skuId: string; qty: number } }
  | { type: 'CLEAR' };

const STORAGE_KEY = 'cart_v1';

function loadInitialState(): CartState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (_) {}
  return { items: [] };
}

function reducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case 'ADD_ITEM': {
      const qty = Math.max(1, action.payload.qty ?? 1);
      const idx = state.items.findIndex((i) => i.skuId === action.payload.skuId);
      if (idx >= 0) {
        const updated = [...state.items];
        updated[idx] = { ...updated[idx], qty: updated[idx].qty + qty };
        return { items: updated };
      }
      return {
        items: [
          ...state.items,
          {
            skuId: action.payload.skuId,
            productName: action.payload.productName,
            variantName: action.payload.variantName,
            qty,
            unitPriceCents: action.payload.unitPriceCents,
            image: action.payload.image,
            slug: action.payload.slug,
            isKit: action.payload.isKit,
            includes: action.payload.includes,
          },
        ],
      };
    }
    case 'REMOVE_ITEM': {
      return { items: state.items.filter((i) => i.skuId !== action.payload.skuId) };
    }
    case 'SET_QTY': {
      const { skuId, qty } = action.payload;
      if (qty <= 0) return { items: state.items.filter((i) => i.skuId !== skuId) };
      return {
        items: state.items.map((i) => (i.skuId === skuId ? { ...i, qty } : i)),
      };
    }
    case 'CLEAR':
      return { items: [] };
    default:
      return state;
  }
}

type CartContextValue = {
  isCartOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  items: CartItem[];
  addItem: (item: AddItemPayload) => void;
  removeItem: (skuId: string) => void;
  setQty: (skuId: string, qty: number) => void;
  clear: () => void;
  subtotalCents: number;
  totalQty: number;
};

const CartContext = createContext<CartContextValue | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, undefined, loadInitialState);
  const [isCartOpen, setIsCartOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (_) {}
  }, [state, isCartOpen]);

  const value = useMemo<CartContextValue>(() => {
    const openCart = () => setIsCartOpen(true);
    const closeCart = () => setIsCartOpen(false);
    const subtotalCents = state.items.reduce((acc, i) => acc + i.unitPriceCents * i.qty, 0);
    const totalQty = state.items.reduce((acc, i) => acc + i.qty, 0);
    return {
      isCartOpen,
      openCart,
      closeCart,
      items: state.items,
      addItem: (payload) => {
        dispatch({ type: 'ADD_ITEM', payload });
        try {
          const event = new CustomEvent('cart:add', {
            detail: {
              skuId: payload.skuId,
              qty: Math.max(1, payload.qty ?? 1),
              productName: payload.productName,
              variantName: payload.variantName,
              unitPriceCents: payload.unitPriceCents,
              image: payload.image,
              slug: payload.slug,
              isKit: payload.isKit,
              includes: payload.includes,
            },
          });
          window.dispatchEvent(event);
          trackAddToCart({
            sku: payload.skuId,
            name: payload.productName,
            variant: payload.variantName,
            price: payload.unitPriceCents / 100,
            qty: Math.max(1, payload.qty ?? 1),
          });
        } catch (_) {}
      },
      removeItem: (skuId) => dispatch({ type: 'REMOVE_ITEM', payload: { skuId } }),
      setQty: (skuId, qty) => dispatch({ type: 'SET_QTY', payload: { skuId, qty } }),
      clear: () => dispatch({ type: 'CLEAR' }),
      subtotalCents,
      totalQty,
    };
  }, [state, isCartOpen]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
